import { useNode, Element } from "@craftjs/core";
import React, { useRef } from "react";
import { Box, Typography } from "@mui/material";
import { useResponsive } from "../../hooks/useResponsive";
import { Text } from "./Text";
import { selectedOutlineSx } from "../../config/settingsStyles";
import {
  TextField,
  SelectField,
  ColorField,
  SliderField,
  CheckboxField,
  SettingsSection,
} from "../ui/form/SettingsField";
import { ImageUpload } from "../ui/ImageUpload";
import { getAvatarUrl } from "../../utils/doctorAvatars";

export interface DoctorCardProps {
  name: string;
  specialty: string;
  imageUrl: string;
  description?: string;
  gender?: "M" | "F" | null;
  certificateNo?: string;
  backgroundColor?: string;
  textColor?: string;
  borderRadius?: number;
  showShadow?: boolean;
  imageSize?: "small" | "medium" | "large";
  layout?: "vertical" | "horizontal";
  /** Provider ID from API - used for syncing */
  providerId?: number | null;
  /** @deprecated - kept for backward compatibility with old templates */
  title?: string;
}

// Image size map
const imageSizeMap = {
  small: { desktop: 80, mobile: 64 },
  medium: { desktop: 120, mobile: 80 },
  large: { desktop: 160, mobile: 100 },
};

export const DoctorCard = ({
  name = "Dr. Name",
  specialty = "Specialty",
  imageUrl = "",
  description,
  gender,
  certificateNo,
  backgroundColor = "#ffffff",
  textColor = "#1f2937",
  borderRadius = 12,
  showShadow = true,
  imageSize = "medium",
  layout = "vertical",
  providerId: _providerId, // from API sync, not displayed
  title: _title, // deprecated, ignored
}: DoctorCardProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const { isMobile } = useResponsive(containerRef as React.RefObject<HTMLElement>);

  const {
    connectors: { connect, drag },
    selected,
    actions: { setProp },
  } = useNode((state) => ({
    selected: state.events.selected,
  }));

  const sizes = imageSizeMap[imageSize || "medium"];
  const isHorizontal = layout === "horizontal";

  return (
    <Box
      ref={(ref: HTMLDivElement | null) => {
        containerRef.current = ref;
        if (ref) connect(drag(ref));
      }}
      className="doctor-card"
      sx={{
        bgcolor: backgroundColor,
        borderRadius: `${borderRadius}px`,
        p: isMobile ? "16px" : "24px",
        boxShadow: showShadow
          ? "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)"
          : "none",
        // Works in both Container (flex) and Doctors (carousel)
        flex: "0 1 180px",
        minWidth: 100,
        maxWidth: 220,
        width: "180px",
        boxSizing: "border-box",
        cursor: "pointer",
        ...(selected && selectedOutlineSx),
      }}
    >
      <Box
        sx={{
          display: isMobile ? "block" : isHorizontal ? "flex" : "block",
          textAlign: isMobile ? "center" : isHorizontal ? "left" : "center",
          alignItems: isMobile ? "initial" : isHorizontal ? "center" : "initial",
          gap: isMobile ? 0 : isHorizontal ? "16px" : 0,
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: isMobile ? "center" : isHorizontal ? "flex-start" : "center",
            mb: isMobile ? "16px" : isHorizontal ? 0 : "16px",
          }}
        >
          <Box
            component="img"
            src={getAvatarUrl(imageUrl, gender)}
            alt={name}
            sx={{
              borderRadius: "50%",
              objectFit: "cover",
              width: isMobile ? sizes.mobile : sizes.desktop,
              height: isMobile ? sizes.mobile : sizes.desktop,
              flexShrink: 0,
            }}
          />
        </Box>
        <Box sx={{ minWidth: 0, flex: isMobile ? "none" : isHorizontal ? 1 : "none" }}>
          <Element
            id="doctor-name"
            is={Text}
            text={name}
            fontSize={20}
            fontWeight="semibold"
            tagName="h4"
            color={textColor}
          />
          <Element
            id="doctor-specialty"
            is={Text}
            text={specialty}
            fontSize={14}
            tagName="p"
            color="#6b7280"
          />
          {certificateNo && (
            <Typography
              sx={{
                color: "#9ca3af",
                fontSize: isMobile ? "0.7rem" : "0.75rem",
                mb: description ? 0.5 : 0,
                wordWrap: "break-word",
              }}
            >
              {certificateNo}
            </Typography>
          )}
          {description && (
            <Element
              id="doctor-desc"
              is={Text}
              text={description}
              fontSize={14}
              tagName="p"
              color={textColor}
            />
          )}
        </Box>
      </Box>
    </Box>
  );
};

// Settings component
export const DoctorCardSettings = () => {
  const {
    actions: { setProp },
    imageUrl,
    gender,
    certificateNo,
    backgroundColor,
    textColor,
    borderRadius,
    showShadow,
    imageSize,
    layout,
  } = useNode((node) => ({
    imageUrl: node.data.props.imageUrl,
    gender: node.data.props.gender,
    certificateNo: node.data.props.certificateNo,
    backgroundColor: node.data.props.backgroundColor,
    textColor: node.data.props.textColor,
    borderRadius: node.data.props.borderRadius,
    showShadow: node.data.props.showShadow,
    imageSize: node.data.props.imageSize,
    layout: node.data.props.layout,
  }));

  return (
    <SettingsSection>
      <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: "block" }}>
        💡 คลิกที่ตัวอักษรบน canvas เพื่อแก้ไข
      </Typography>

      <ImageUpload
        label="Photo"
        value={imageUrl}
        onChange={(url) => setProp((props: DoctorCardProps) => (props.imageUrl = url))}
      />

      <SelectField
        label="Gender"
        value={gender || ""}
        onChange={(v) => setProp((props: DoctorCardProps) => (props.gender = v as "M" | "F" | null || null))}
        options={[
          { value: "", label: "Not specified" },
          { value: "M", label: "Male" },
          { value: "F", label: "Female" },
        ]}
      />

      <TextField
        label="License No."
        value={certificateNo || ""}
        onChange={(v) => setProp((props: DoctorCardProps) => (props.certificateNo = v || undefined))}
        placeholder="ว.12345"
      />

      <SelectField
        label="Layout"
        value={layout}
        onChange={(v) => setProp((props: DoctorCardProps) => (props.layout = v as "vertical" | "horizontal"))}
        options={[
          { value: "vertical", label: "Vertical" },
          { value: "horizontal", label: "Horizontal" },
        ]}
      />

      <SelectField
        label="Image Size"
        value={imageSize}
        onChange={(v) => setProp((props: DoctorCardProps) => (props.imageSize = v as DoctorCardProps["imageSize"]))}
        options={[
          { value: "small", label: "Small" },
          { value: "medium", label: "Medium" },
          { value: "large", label: "Large" },
        ]}
      />

      <ColorField
        label="Background"
        value={backgroundColor}
        onChange={(v) => setProp((props: DoctorCardProps) => (props.backgroundColor = v))}
      />

      <ColorField
        label="Text Color"
        value={textColor}
        onChange={(v) => setProp((props: DoctorCardProps) => (props.textColor = v))}
      />

      <SliderField
        label="Border Radius"
        value={borderRadius}
        onChange={(v) => setProp((props: DoctorCardProps) => (props.borderRadius = v))}
        min={0}
        max={24}
        unit="px"
      />

      <CheckboxField
        label="Show Shadow"
        checked={showShadow}
        onChange={(v) => setProp((props: DoctorCardProps) => (props.showShadow = v))}
      />
    </SettingsSection>
  );
};

DoctorCard.craft = {
  displayName: "Doctor Card",
  isCanvas: true,
  props: {
    name: "Dr. Name",
    specialty: "Specialty",
    imageUrl: "",
    gender: null,
    certificateNo: "",
    backgroundColor: "#ffffff",
    textColor: "#1f2937",
    borderRadius: 12,
    showShadow: true,
    imageSize: "medium",
    layout: "vertical",
    providerId: null,
  },
  rules: {
    canMoveIn: () => false,
    canMoveOut: () => false,
  },
  related: {
    settings: DoctorCardSettings,
  },
};
